import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  Clock, 
  X, 
  Receipt, 
  CheckCircle2 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

export default function AdmissionsFeesPaymentForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({ 
    title: '', 
    subtitle: '', 
    description: '', 
    blocks: [] 
  });
  const [pendingFields, setPendingFields] = useState({ 
    title: false, 
    subtitle: false, 
    description: false 
  });
  const [pendingFees, setPendingFees] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});

  // Modal State
  const [isAddingFee, setIsAddingFee] = useState(false);
  const [editingFeeIndex, setEditingFeeIndex] = useState(null);
  const [modalFeeData, setModalFeeData] = useState(null);
  const [newItemText, setNewItemText] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadData = async () => {
    setLoading(true);
    try {
      let section = null;

      // 1. Fetch admissions sections via admin endpoint
      try {
        const response = await fetch(`${apiBase}/admissions/cms/sections`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (result.ok && Array.isArray(result.data)) {
            section = result.data.find(s => s.section_type === 'admissions-fees-payment');
          }
        }
      } catch (err) {
        console.warn('[AdmissionsFeesPaymentForm] Admin fetch warning:', err.message);
      }

      // 2. Fallback to public query if needed
      if (!section) {
        try {
          const pubRes = await fetch(`${apiBase}/admissions/cms/sections?published=true`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pubRes.ok) {
            const pubResult = await pubRes.json();
            if (pubResult.ok && Array.isArray(pubResult.data)) {
              section = pubResult.data.find(s => s.section_type === 'admissions-fees-payment');
            }
          }
        } catch (pubErr) {
          console.warn('[AdmissionsFeesPaymentForm] Public fetch warning:', pubErr.message);
        }
      }

      if (!section) {
        throw new Error('Fees & Payment Information section not found');
      }

      setSectionId(section.id);

      let content = section.content_published || section.content || {};
      if (typeof content === 'string') {
        try { content = JSON.parse(content); } catch (e) { content = {}; }
      }
      const en = content?.en || content || {};
      const rawBlocks = Array.isArray(en.blocks) ? en.blocks : (Array.isArray(en.feesPayment) ? en.feesPayment : []);
      const cleanBlocks = rawBlocks
        .filter(b => b && typeof b === 'object')
        .map(b => ({
          title: b.title || '',
          icon: b.icon || 'fa-credit-card',
          summary: b.summary || '',
          description: b.description || '',
          items: Array.isArray(b.items) ? b.items : [],
          highlight: b.highlight || ''
        }));

      const initialData = {
        title: en.title || '',
        subtitle: en.subtitle || en.kicker || '',
        description: en.description || '',
        blocks: cleanBlocks
      };

      const pMap = { title: false, subtitle: false, description: false };
      const fMap = {};

      // 3. Check for pending changes in workflow
      try {
        const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (pRes.ok) {
          const pResult = await pRes.json();
          if (pResult.success && Array.isArray(pResult.data?.pending_changes)) {
            pResult.data.pending_changes.forEach(c => {
              if (c.status === 'pending' && (c.locale === 'en' || !c.locale)) {
                let val = c.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (['title', 'subtitle', 'description'].includes(c.field_id)) {
                  pMap[c.field_id] = true;
                  if (typeof val === 'string') {
                    initialData[c.field_id] = val;
                  }
                } else if (c.field_id && (c.field_id.includes('blocks.') || c.field_id.includes('feesPayment.'))) {
                  const m = c.field_id.match(/(?:blocks|feesPayment)\.(\d+)\.(add|update|delete)/);
                  if (m) {
                    const feeIdx = parseInt(m[1], 10);
                    const op = m[2];
                    fMap[`fee_${feeIdx}`] = true;
                    if (op === 'delete') {
                      fMap[`fee_${feeIdx}_delete`] = true;
                    } else if (op === 'add') {
                      fMap[`fee_${feeIdx}_add`] = true;
                      if (val && !initialData.blocks[feeIdx]) {
                        initialData.blocks.push({
                          title: val.title || '',
                          icon: val.icon || 'fa-credit-card',
                          summary: val.summary || '',
                          description: val.description || '',
                          items: Array.isArray(val.items) ? val.items : [],
                          highlight: val.highlight || ''
                        });
                      }
                    } else if (op === 'update') {
                      if (val && initialData.blocks[feeIdx]) {
                        initialData.blocks[feeIdx] = { 
                          ...initialData.blocks[feeIdx], 
                          ...val,
                          items: Array.isArray(val.items) ? val.items : (initialData.blocks[feeIdx].items || [])
                        };
                      }
                    }
                  }
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('[AdmissionsFeesPaymentForm] Pending changes check warning:', pErr.message);
      }

      setFormData(initialData);
      setOriginalData(JSON.parse(JSON.stringify(initialData)));
      setPendingFields(pMap);
      setPendingFees(fMap);
    } catch (err) {
      console.error('[AdmissionsFeesPaymentForm] Load error:', err.message);
      toast.error('Failed to load admissions fees data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitField = async (name, label) => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (formData[name] === originalData[name]) { 
      toast.warning('No changes to submit'); 
      return; 
    }

    setSubmitting(p => ({ ...p, [name]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: label || `Fees & Payment ${name.replace(/_/g, ' ')}`,
          old_value: originalData[name] || '',
          new_value: formData[name] || '',
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Submitted ${label || name} for Principal approval`);
        setPendingFields(p => ({ ...p, [name]: true }));
        setOriginalData(p => ({ ...p, [name]: formData[name] }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for ${label || name} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [name]: false }));
    }
  };

  // Open Add Fee Category Modal
  const handleOpenAddFee = () => {
    if (formData.blocks.length >= 8) {
      toast.warning('Maximum 8 fee categories allowed');
      return;
    }
    const newFee = { 
      title: '', 
      icon: 'fa-credit-card', 
      summary: '', 
      description: '', 
      items: [], 
      highlight: '' 
    };
    setIsAddingFee(true);
    setEditingFeeIndex(formData.blocks.length);
    setModalFeeData(newFee);
    setNewItemText('');
  };

  // Open Edit Fee Category Modal
  const handleOpenEditFee = (idx) => {
    const target = formData.blocks[idx];
    if (!target) return;
    setIsAddingFee(false);
    setEditingFeeIndex(idx);
    setModalFeeData({
      title: target.title || '',
      icon: target.icon || 'fa-credit-card',
      summary: target.summary || '',
      description: target.description || '',
      items: Array.isArray(target.items) ? [...target.items] : [],
      highlight: target.highlight || ''
    });
    setNewItemText('');
  };

  const handleCloseModal = () => {
    setIsAddingFee(false);
    setEditingFeeIndex(null);
    setModalFeeData(null);
    setNewItemText('');
  };

  const handleModalFeeChange = (field, value) => {
    if (!modalFeeData) return;
    setModalFeeData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddItem = () => {
    if (!newItemText.trim() || !modalFeeData) return;
    setModalFeeData(prev => ({
      ...prev,
      items: [...(prev.items || []), newItemText.trim()]
    }));
    setNewItemText('');
  };

  const handleRemoveItem = (itemIdx) => {
    if (!modalFeeData) return;
    setModalFeeData(prev => ({
      ...prev,
      items: (prev.items || []).filter((_, i) => i !== itemIdx)
    }));
  };

  // Submit Fee Category from Modal (Add or Update)
  const handleSaveFeeModal = async () => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (!modalFeeData || !modalFeeData.title?.trim()) { 
      toast.warning('Category title is required'); 
      return; 
    }

    const idx = editingFeeIndex;
    const isNew = isAddingFee;
    const op = isNew ? 'add' : 'update';
    const submitKey = isNew ? 'add_fee' : `fee_${idx}`;

    setSubmitting(p => ({ ...p, [submitKey]: true }));

    const payload = {
      title: modalFeeData.title.trim(),
      icon: modalFeeData.icon || 'fa-credit-card',
      summary: modalFeeData.summary || '',
      description: modalFeeData.description || '',
      items: Array.isArray(modalFeeData.items) ? modalFeeData.items : [],
      highlight: modalFeeData.highlight || ''
    };

    const fieldId = `blocks.${idx}.${op}`;
    const fieldName = `${isNew ? 'Add' : 'Update'} Fee Category: ${payload.title}`;
    const oldValue = isNew ? null : (originalData.blocks[idx] || {});

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: fieldName,
          old_value: oldValue,
          new_value: payload,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Fee category "${payload.title}" submitted for Principal approval`);
        setPendingFees(p => ({
          ...p,
          [`fee_${idx}`]: true,
          [`fee_${idx}_add`]: isNew
        }));

        if (isNew) {
          setFormData(prev => ({
            ...prev,
            blocks: [...prev.blocks, { ...payload }]
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            blocks: prev.blocks.map((b, i) => (i === idx ? { ...payload } : b))
          }));
        }

        handleCloseModal();
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for this category is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting fee category: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [submitKey]: false }));
    }
  };

  // Propose Deletion of Fee Category (Submitted to Principal!)
  const handleDeleteFee = async (idx) => {
    const targetFee = formData.blocks[idx];
    if (!targetFee) return;

    if (!window.confirm(`Are you sure you want to propose deleting "${targetFee.title || `Category ${idx + 1}`}"? This will require Principal approval.`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const key = `del_fee_${idx}`;
    setSubmitting(p => ({ ...p, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `blocks.${idx}.delete`,
          field_name: `Delete Fee Category: ${targetFee.title || `Category ${idx + 1}`}`,
          old_value: targetFee,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Deletion of "${targetFee.title || `Category ${idx + 1}`}" submitted for Principal approval`);
        setPendingFees(p => ({
          ...p,
          [`fee_${idx}`]: true,
          [`fee_${idx}_delete`]: true
        }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for this category is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Delete proposal failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting delete proposal: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading admissions fees & payment configuration...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <CreditCard size={22} />
        </div>
        <div>
          <h2 className="unified-title">Fees & Payment Information</h2>
          <p className="unified-subtitle">
            Manage fee structures, tuition options, installment plans, and transparent pricing policies
          </p>
        </div>
      </div>

      {/* Section Subtitle Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Tag / Subtitle</label>
            <p className="unified-field-desc">Kicker label above the fees heading (e.g. Fees & Payment Information)</p>
          </div>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={submitting.subtitle || pendingFields.subtitle}
            placeholder="e.g. Fees & Payment Information"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', 'Fees Subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} />
            {submitting.subtitle ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Title Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Main Section Title</label>
            <p className="unified-field-desc">Headline introducing tuition and payment options</p>
          </div>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Transparent Fees, Flexible Plans & Clear Payment Options"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', 'Fees Title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} />
            {submitting.title ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Description Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Description</label>
            <p className="unified-field-desc">Overview statement confirming no hidden costs and transparent pricing</p>
          </div>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="e.g. A comprehensive overview of admission fees, tuition, and payment options for each level..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('description', 'Fees Description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} />
            {submitting.description ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Fee Category Blocks Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Fee Structure Categories ({formData.blocks.length})</label>
            <p className="unified-field-desc">Blocks detailing registration fees, tuition schedules, and payment methods</p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleOpenAddFee}
          >
            <Plus size={14} /> Add Fee Category
          </button>
        </div>

        {formData.blocks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <CreditCard size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No fee categories configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Fee Category" above to configure fee items</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
            {formData.blocks.map((fee, idx) => {
              const key = `fee_${idx}`;
              const isPending = pendingFees[key];
              const isPendingDelete = pendingFees[`fee_${idx}_delete`];
              const isPendingAdd = pendingFees[`fee_${idx}_add`];
              const itemsCount = Array.isArray(fee.items) ? fee.items.length : 0;

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    padding: '16px 20px',
                    background: isPendingDelete ? '#fff1f2' : '#f8fafc',
                    borderRadius: '12px',
                    border: `1px solid ${isPendingDelete ? '#fecdd3' : '#e2e8f0'}`,
                    transition: 'all 0.2s ease',
                    opacity: isPendingDelete ? 0.8 : 1
                  }}
                >
                  <div
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '10px',
                      background: isPendingDelete ? '#fee2e2' : '#ecfdf5',
                      color: isPendingDelete ? '#ef4444' : '#059669',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Receipt size={20} />
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <h4 style={{ margin: 0, fontSize: '15.5px', fontWeight: 800, color: '#0f172a' }}>
                        {fee.title || `Fee Category ${idx + 1}`}
                      </h4>
                      {isPendingDelete ? (
                        <span 
                          className="unified-pending-pill" 
                          style={{ padding: '2px 8px', fontSize: '10px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}
                        >
                          <Trash2 size={10} /> Pending Deletion
                        </span>
                      ) : isPendingAdd ? (
                        <span 
                          className="unified-pending-pill" 
                          style={{ padding: '2px 8px', fontSize: '10px', background: '#dcfce7', color: '#15803d', border: '1px solid #86efac' }}
                        >
                          <Plus size={10} /> Proposed New
                        </span>
                      ) : isPending ? (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      ) : null}
                    </div>

                    {fee.summary && (
                      <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#334155', fontWeight: 600 }}>
                        {fee.summary}
                      </p>
                    )}

                    {fee.description && (
                      <p style={{ margin: '0 0 10px', fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 }}>
                        {fee.description}
                      </p>
                    )}

                    {itemsCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>
                          Includes ({itemsCount}):
                        </span>
                        {fee.items.slice(0, 3).map((item, ii) => (
                          <span
                            key={ii}
                            style={{
                              fontSize: '11px',
                              background: '#f1f5f9',
                              color: '#334155',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontWeight: 500
                            }}
                          >
                            • {item}
                          </span>
                        ))}
                        {itemsCount > 3 && (
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>+{itemsCount - 3} more</span>
                        )}
                      </div>
                    )}

                    {fee.highlight && (
                      <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span
                          style={{
                            fontSize: '11px',
                            background: '#fef3c7',
                            color: '#92400e',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontWeight: 600
                          }}
                        >
                          Highlight: {fee.highlight}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleOpenEditFee(idx)}
                      disabled={isPendingDelete || isPending}
                    >
                      <Edit3 size={14} /> Edit
                    </button>
                    {!isPendingDelete && (
                      <button
                        type="button"
                        onClick={() => handleDeleteFee(idx)}
                        disabled={submitting[`del_fee_${idx}`] || isPendingDelete}
                        style={{
                          padding: '8px 12px',
                          color: '#ef4444',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          cursor: (submitting[`del_fee_${idx}`] || isPendingDelete) ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontWeight: 600,
                          fontSize: '12px'
                        }}
                        title="Propose deletion of this fee category"
                      >
                        <Trash2 size={14} />
                        <span>{submitting[`del_fee_${idx}`] ? 'Submitting...' : 'Delete'}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Fee Category Modal */}
      {(isAddingFee || editingFeeIndex !== null) && modalFeeData && (
        <div
          className="unified-modal-overlay"
          onClick={handleCloseModal}
        >
          <div className="unified-modal" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                  {isAddingFee ? 'Add Fee Category' : `Edit Fee Category: ${modalFeeData.title || `Category ${(editingFeeIndex || 0) + 1}`}`}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure fee category description, itemized costs, and highlight notice
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Title & Icon */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Category Title *
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalFeeData.title || ''}
                    onChange={(e) => handleModalFeeChange('title', e.target.value)}
                    placeholder="e.g. Registration & Admission Fees or Tuition & Payment Plans"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Icon
                  </label>
                  <select
                    className="unified-input"
                    value={modalFeeData.icon || 'fa-credit-card'}
                    onChange={(e) => handleModalFeeChange('icon', e.target.value)}
                  >
                    <option value="fa-credit-card">Credit Card / Tuition</option>
                    <option value="fa-clipboard-list">Clipboard / Registration</option>
                    <option value="fa-medal">Medal / Scholarship</option>
                    <option value="fa-star">Star / Services</option>
                    <option value="fa-wallet">Wallet / Cashier</option>
                  </select>
                </div>
              </div>

              {/* Summary */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Short Summary
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalFeeData.summary || ''}
                  onChange={(e) => handleModalFeeChange('summary', e.target.value)}
                  placeholder="e.g. Clear onboarding fees so families know what to expect before enrollment."
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Full Description
                </label>
                <textarea
                  className="unified-textarea"
                  rows={3}
                  value={modalFeeData.description || ''}
                  onChange={(e) => handleModalFeeChange('description', e.target.value)}
                  placeholder="Comprehensive details on what this fee category covers..."
                />
              </div>

              {/* Itemized Points / Fees */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                  Included Items & Cost Points
                </label>

                {/* Add Item Row */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddItem())}
                    placeholder="e.g. Application and assessment fee (non-refundable)..."
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={handleAddItem}
                    style={{ padding: '8px 16px', fontSize: '12.5px' }}
                  >
                    <Plus size={14} /> Add Item
                  </button>
                </div>

                {/* Items List */}
                {modalFeeData.items && modalFeeData.items.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {modalFeeData.items.map((item, ii) => (
                      <div
                        key={ii}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 12px',
                          background: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '12.5px',
                          color: '#1e293b'
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <CheckCircle2 size={13} color="#2563eb" />
                          {item}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(ii)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#ef4444',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '2px 4px'
                          }}
                          aria-label={`Remove ${item}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: '12.5px', color: '#94a3b8' }}>
                    No fee items listed yet. Type in the box above to add fee bullet points.
                  </p>
                )}
              </div>

              {/* Highlight Note */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Important Highlight / Policy Notice
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalFeeData.highlight || ''}
                  onChange={(e) => handleModalFeeChange('highlight', e.target.value)}
                  placeholder="e.g. Transparent pricing with no hidden charges"
                />
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                disabled={submitting[isAddingFee ? 'add_fee' : `fee_${editingFeeIndex}`] || !modalFeeData.title?.trim()}
                onClick={handleSaveFeeModal}
              >
                <Save size={14} />
                {submitting[isAddingFee ? 'add_fee' : `fee_${editingFeeIndex}`]
                  ? 'Submitting...'
                  : (isAddingFee ? 'Submit New Category' : 'Save & Submit Category')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
